"use client";

import { SELLER_PHOTO_COACH_DISMISS_KEY } from "@/components/listing/SellerPhotoCoach";
import { dispatchNotificationSetupBannerStateChanged } from "@/lib/notifications/notification-setup-banner-events";
import { resetNotificationSetupBannerClientState } from "@/lib/notifications/notification-setup-banner-policy";
import { ONBOARDING_STORAGE_KEYS } from "@/lib/onboarding";
import type { DevToolboxScenarioId } from "@/lib/devToolbox/types";

export const NEW_USER_WALKTHROUGH_SESSION_KEY =
  "dev-toolbox-new-user-walkthrough-v1";

export type NewUserWalkthroughStep = {
  order: number;
  label: string;
  hint: string;
  route: string;
  scenarioId?: DevToolboxScenarioId;
};

/** Ordered path a brand-new visitor → registered user typically sees. */
export const NEW_USER_WALKTHROUGH_STEPS: NewUserWalkthroughStep[] = [
  {
    order: 1,
    label: "Homepage welcome tour",
    hint: "Guest tour on the homepage (starts automatically).",
    route: "/",
    scenarioId: "onboarding.flow.new-user-journey",
  },
  {
    order: 2,
    label: "Sign up",
    hint: "Create-account form and market-partner fields.",
    route: "/signup",
  },
  {
    order: 3,
    label: "Verify email",
    hint: "OTP screen after signup (dev preview).",
    route: "/verify-account?email=dev@sellitoff.local&devPreview=onboarding",
    scenarioId: "onboarding.registration.verify-account",
  },
  {
    order: 4,
    label: "Complete profile",
    hint: "Required details after Google or email signup.",
    route: "/complete-profile?email=dev@sellitoff.local",
    scenarioId: "google-profile.empty-required",
  },
  {
    order: 5,
    label: "Near-me alerts",
    hint: "Registration onboarding step 1 — notification preferences.",
    route: "/onboarding/notifications?devPreview=onboarding",
    scenarioId: "onboarding.registration.notifications",
  },
  {
    order: 6,
    label: "Choose first action",
    hint: "Registration onboarding step 2 — browse vs sell.",
    route: "/onboarding/destination?devPreview=onboarding",
    scenarioId: "onboarding.registration.destination",
  },
  {
    order: 7,
    label: "Market tips banner",
    hint: "First visit to the marketplace.",
    route: "/market",
    scenarioId: "onboarding.prompt.market-intro",
  },
  {
    order: 8,
    label: "Sell intro modal",
    hint: "Before-you-list guidance on the sell page.",
    route: "/sell",
    scenarioId: "onboarding.prompt.sell-intro",
  },
  {
    order: 9,
    label: "My Items status intro",
    hint: "Listing status explainer (requires sign-in).",
    route: "/my-items",
    scenarioId: "onboarding.prompt.my-items-status",
  },
];

const removeStorageKey = (key: string, storage: Storage) => {
  if (typeof window === "undefined") return;
  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
};

/** Clears client flags so tours, banners, and prompts show like a first visit. */
export function resetNewUserWalkthroughClientState(): void {
  if (typeof window === "undefined") return;

  Object.values(ONBOARDING_STORAGE_KEYS).forEach((key) => {
    removeStorageKey(key, localStorage);
  });

  resetNotificationSetupBannerClientState();
  dispatchNotificationSetupBannerStateChanged();

  removeStorageKey(SELLER_PHOTO_COACH_DISMISS_KEY, sessionStorage);
  removeStorageKey("pwaPromptLastShown", localStorage);
  removeStorageKey("pwaEngagementCount", localStorage);
}

export function enableNewUserWalkthrough(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(NEW_USER_WALKTHROUGH_SESSION_KEY, "true");
  } catch {
    // Ignore storage failures.
  }
}

export function disableNewUserWalkthrough(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(NEW_USER_WALKTHROUGH_SESSION_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function isNewUserWalkthroughActive(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(NEW_USER_WALKTHROUGH_SESSION_KEY) === "true";
  } catch {
    return false;
  }
}
